<template>
  <div class="cart-form__secondary">
    <h3 class="cart-form__secondary-title">Billing Address</h3>
    <div class="cart-form__group-wrapper">
      <div class="cart-form__group cart-form__group--short">
        <input
          :type="fields['street address'].type"
          :class="['ine-input', fieldHasError('street address') && 'ine-input--error']"
          :placeholder="fields['street address'].placeholder"
          :autocomplete="fields['street address'].autocomplete"
          :data-recurly="fields['street address']['data-recurly']"
          @focus="clearError('street address')"
          v-model="streetAddress"
        />
        <span v-if="fieldHasError('street address')" class="ine-input__error-msg">{{
          errors['street address']
        }}</span>
      </div>
      <div class="cart-form__group cart-form__group--short">
        <input
          :type="fields.city.type"
          :class="['ine-input', fieldHasError('city') && 'ine-input--error']"
          :placeholder="fields.city.placeholder"
          :autocomplete="fields.city.autocomplete"
          :data-recurly="fields.city['data-recurly']"
          @focus="clearError('city')"
          v-model="city"
        />
        <span v-if="fieldHasError('city')" class="ine-input__error-msg">{{ errors.city }}</span>
      </div>
    </div>
    <div class="cart-form__group-wrapper">
      <div class="cart-form__group cart-form__group--short">
        <input
          :type="fields.state.type"
          :class="['ine-input', fieldHasError('state') && 'ine-input--error']"
          :placeholder="fields.state.placeholder"
          :autocomplete="fields.state.autocomplete"
          :data-recurly="fields.state['data-recurly']"
          @focus="clearError('state')"
          v-model="state"
        />
        <span v-if="fieldHasError('state')" class="ine-input__error-msg">{{ errors.state }}</span>
      </div>
      <div class="cart-form__group cart-form__group--short">
        <input
          :type="fields.zip.type"
          :class="['ine-input', fieldHasError('zip') && 'ine-input--error']"
          :placeholder="fields.zip.placeholder"
          :autocomplete="fields.zip.autocomplete"
          :data-recurly="fields.zip['data-recurly']"
          @focus="clearError('zip')"
          v-model="zip"
        />
        <span v-if="fieldHasError('zip')" class="ine-input__error-msg">{{ errors.zip }}</span>
      </div>
    </div>
    <div class="cart-form__group-wrapper">
      <div class="cart-form__group cart-form__group-select cart-form__group--short">
        <v-select
          :class="['ine-input-dropdown__wrapper', fieldHasError('country') && 'ine-input--error']"
          :options="
            countryCodes.map(countryCode => ({
              label: countryCode.name,
              code: countryCode.value,
            }))
          "
          :searchable="true"
          :clearable="false"
          v-model="country"
          placeholder="Country"
        >
          <template #search="{ attributes, events }">
            <input
              readonly
              :required="!country"
              :data-recurly="fields.country['data-recurly']"
              :autocomplete="fields.country.autocomplete"
              :value="countryName"
              v-bind="attributes"
              v-on="events"
            />
          </template>
        </v-select>
        <span v-if="fieldHasError('country')" class="ine-input__error-msg">{{
          errors.country
        }}</span>
      </div>
      <div class="cart-form__group cart-form__group--short">
        <input
          :type="fields.phone.type"
          :class="['ine-input', fieldHasError('phone') && 'ine-input--error']"
          :placeholder="fields.phone.placeholder"
          :autocomplete="fields.phone.autocomplete"
          :data-recurly="fields.phone['data-recurly']"
          @focus="clearError('phone')"
          v-model="phone"
        />
        <span v-if="fieldHasError('phone')" class="ine-input__error-msg">{{ errors.phone }}</span>
      </div>
    </div>
    <div class="cart-form__group cart-form__group-company">
      <input
        :type="fields.company.type"
        :class="['ine-input', fieldHasError('company') && 'ine-input--error']"
        :placeholder="fields.company.placeholder"
        :autocomplete="fields.company.autocomplete"
        :data-recurly="fields.company['data-recurly']"
        @focus="clearError('company')"
        v-model="company"
      />
      <span v-if="fieldHasError('company')" class="ine-input__error-msg">{{ errors.company }}</span>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import countryCodes from '@/static/countryCodes'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'PaymentFormBillingAddress',
  components: {
    vSelect,
  },
  props: {
    fields: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
    fieldHasError: {
      type: Function,
      required: true,
    },
    clearError: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      countryCodes,
    }
  },
  computed: {
    ...mapState({
      paymentEntries: state => state.subscriptions.paymentEntries,
    }),
    country: {
      set(input) {
        const { code } = input
        this.$emit('update-field', { name: 'country', input: code })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'country',
          input: code,
        })
      },
      get() {
        return this.paymentEntries.country
      },
    },
    countryName() {
      const countryName = this.countryCodes.find(
        countryCodesObj => countryCodesObj.value === this.country
      )
      if (countryName) this.clearError('country')
      return countryName ? countryName.name : ''
    },
    streetAddress: {
      set(input) {
        this.$emit('update-field', { name: 'street address', input })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'streetAddress',
          input,
        })
      },
      get() {
        return this.paymentEntries.streetAddress
      },
    },
    city: {
      set(input) {
        this.$emit('update-field', { name: 'city', input })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'city',
          input,
        })
      },
      get() {
        return this.paymentEntries.city
      },
    },
    state: {
      set(input) {
        this.$emit('update-field', { name: 'state', input })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'state',
          input,
        })
      },
      get() {
        return this.paymentEntries.state
      },
    },
    zip: {
      set(input) {
        this.$emit('update-field', { name: 'zip', input })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'zip',
          input,
        })
      },
      get() {
        return this.paymentEntries.zip
      },
    },
    phone: {
      set(input) {
        this.$emit('update-field', { name: 'phone', input })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'phone',
          input,
        })
      },
      get() {
        return this.paymentEntries.phone
      },
    },
    company: {
      set(input) {
        this.$emit('update-field', { name: 'company', input })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'company',
          input,
        })
      },
      get() {
        return this.paymentEntries.company
      },
    },
  },
}
</script>

<style lang="scss">
.cart-form {
  &__group {
    &-company {
      margin-right: 0 !important;
      margin-bottom: 25px !important;
    }
  }
  &__secondary {
    &-title {
      color: var(--white);
      font-family: var(--font-regular);
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      width: 100%;
      text-align: left;
      margin-bottom: 20px;
    }
  }
}
</style>
